/**
 * Подготовка фото перед загрузкой: ВКонтакте не принимает WEBP и очень большие картинки
 * (сумма сторон ≤ 14000 px), поэтому WEBP и огромные фото перекодируются в JPEG прямо в браузере.
 */
import { MAX_IMAGE_BYTES } from "@impuls/shared";

const MAX_SIDE = 5000;

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(file);
    } catch {
      /* запасной путь ниже */
    }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Не удалось прочитать «${file.name}»`));
    img.src = URL.createObjectURL(file);
  });
}

export async function prepareImage(file: File): Promise<File> {
  const okType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
  if (!okType) throw new Error(`«${file.name}»: поддерживаются JPG, PNG и WEBP`);
  if (file.size > MAX_IMAGE_BYTES) throw new Error(`«${file.name}» больше 25 МБ`);
  const needsConvert = file.type === "image/webp";
  if (!needsConvert && file.size < 6 * 1024 * 1024) return file;
  const bmp = await loadBitmap(file);
  const w = "width" in bmp ? bmp.width : 0;
  const h = "height" in bmp ? bmp.height : 0;
  if (!needsConvert && Math.max(w, h) <= MAX_SIDE) return file;
  const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmp as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Не удалось перекодировать фото"))), "image/jpeg", 0.92),
  );
  return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
}
